import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "../../../../util/api";

// MUI
import {
    Box,
    Button,
    Chip,
    Collapse,
    Container,
    Divider,
    InputAdornment,
    Paper,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { notify } from "../../../../util/toast";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

import KeyVisualModal from "./modal/KeyVisualModal";
import LoadingDots from "../../../../component/common/LoadingDots";
import ActionButton from "../../../../component/common/ActionButton";
import { ds } from "../../../../styles/designSystem";
import AppAutocomplete from "../../../../component/common/AppAutocomplete";
import { keyVisualApi } from "../api/keyVisualApi";
import { pickThumbnailSrc } from "../../../../util/imageUrl";

const PAGE_SIZE = 30;

const activeChipSx = ds.components.external.chipActive;
const normalChipSx = ds.components.external.chip;

export default function KeyVisualListPage() {
    const [allKeywords, setAllKeywords] = useState([]);
    const [activeKeywords, setActiveKeywords] = useState([]);
    const [projectOptions, setProjectOptions] = useState([]);
    const [activeProjectName, setActiveProjectName] = useState("");
    const [activeName, setActiveName] = useState("");
    const [posters, setPosters] = useState([]);

    // ✅ 모달 “언마운트로 꺼짐” 방지: id로 유지 + 스냅샷 보관
    const [selectedPosterId, setSelectedPosterId] = useState(null);
    const [selectedPosterSnapshot, setSelectedPosterSnapshot] = useState(null);
    const selectedPosterSnapshotRef = useRef(null);

    const [loadingKeywords, setLoadingKeywords] = useState(false);
    const [loadingPosters, setLoadingPosters] = useState(false);

    // 무한 스크롤 페이지네이션 상태
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const sentinelRef = useRef(null);

    // UI states
    const [keywordOpen, setKeywordOpen] = useState(true);
    const [keywordQuery, setKeywordQuery] = useState("");
    const [showAllKeywords, setShowAllKeywords] = useState(false);
    const [autoCompleteValue, setAutoCompleteValue] = useState(null);


    const API_BASE = process.env.REACT_APP_API_BASE_URL;
    const API_REAL = process.env.REACT_APP_API_REAL_URL;

    const loadKeywords = useCallback(async () => {
        setLoadingKeywords(true);
        try {
            const data = await api.get(`${API_BASE}/key-visual/2d/registered-keywords`);
            setAllKeywords(Array.isArray(data) ? data : (data?.data || []));
            // ✅ activeKeywords는 절대 건드리지 않음(유지)
        } catch (err) {
            console.error(err);
            notify("키워드 목록을 불러오지 못했습니다.", "error");
        } finally {
            setLoadingKeywords(false);
        }
    }, [API_BASE]);

    const loadProjectOptions = useCallback(async () => {
        try {
            const data = await keyVisualApi.listProjectNames();
            setProjectOptions(Array.isArray(data) ? data : (data?.data || []));
        } catch (e) {
            console.error(e);
        }
    }, []);

    /**
     * 페이지 단위 키비주얼 로드 (백엔드 필터 사용).
     * 백엔드가 keywords + projectName 매칭 후 페이지네이션 — 결과는 항상 정확.
     * append=true → 누적, false → 첫 로드.
     */
    const loadPostersPage = useCallback(async (pageNum, append, keywordsArg, projectNameArg, nameArg) => {
        setLoadingPosters(true);
        try {
            const data = await keyVisualApi.getAllPaged({
                page: pageNum,
                size: PAGE_SIZE,
                keywords: keywordsArg,
                projectName: projectNameArg,
                name: nameArg,
            });
            const items = data?.content ?? [];
            setPosters((prev) => append ? [...prev, ...items] : items);
            setHasMore(pageNum + 1 < (data?.totalPages ?? 0));
            setPage(pageNum);
        } catch (err) {
            console.error(err);
            notify("키비주얼 목록을 불러오지 못했습니다.", "error");
            // 에러 시 무한 스크롤 중단 — 안 그러면 sentinel 이 계속 보여 useEffect 가 재호출 루프 탐.
            setHasMore(false);
        } finally {
            setLoadingPosters(false);
        }
    }, []);

    const reloadDatas = useCallback(
        async (silent = false) => {
            await Promise.all([
                loadPostersPage(0, false, activeKeywords, activeProjectName, activeName),
                loadKeywords(),
                loadProjectOptions(),
            ]);
            if (!silent) notify("새로고침 완료", "success");
        },
        [loadPostersPage, loadKeywords, loadProjectOptions, activeKeywords, activeProjectName, activeName]
    );

    // 마운트 1회 — 첫 페이지 + 키워드/프로젝트 옵션 로드
    useEffect(() => {
        loadPostersPage(0, false, [], "", "");
        loadKeywords();
        loadProjectOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 활성 키워드/프로젝트명/이름 변경 시 백엔드 필터로 첫 페이지부터 재조회
    useEffect(() => {
        loadPostersPage(0, false, activeKeywords, activeProjectName, activeName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeKeywords, activeProjectName, activeName]);

    // 무한 스크롤 — 백엔드가 필터 후 페이지네이션하므로 항상 동작.
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !hasMore || loadingPosters) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                loadPostersPage(page + 1, true, activeKeywords, activeProjectName, activeName);
            }
        }, { threshold: 0.1, rootMargin: "200px" });
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, loadingPosters, page, loadPostersPage, activeKeywords, activeProjectName, activeName]);

    // 백엔드가 activeKeywords 필터를 처리하므로 posters 가 이미 필터된 결과.
    // filteredPosters 는 호환성 유지용 alias.
    const filteredPosters = posters;

    // ✅ 선택중인 키비주얼: posters에서 찾아보고 없으면 스냅샷 유지 (모달 안 꺼짐)
    const selectedPoster = useMemo(() => {
        if (!selectedPosterId) return null;
        const found = posters.find((p) => p.id === selectedPosterId);
        return found || selectedPosterSnapshotRef.current || selectedPosterSnapshot || null;
    }, [posters, selectedPosterId, selectedPosterSnapshot]);

    const toggleKeyword = useCallback((keyword) => {
        setActiveKeywords((prev) =>
            prev.includes(keyword) ? prev.filter((k) => k !== keyword) : [...prev, keyword]
        );
    }, []);

    const clearKeywords = () => setActiveKeywords([]);

    // ✅ "저장 즉시 부분 반영" — 전체 리프래시 없음
    // - posters 의 해당 row 만 patch
    // - 열린 모달 스냅샷도 patch
    // - 전체 키워드 목록도 in-memory 로 patch (loadKeywords 재호출 X)
    //   → 새 키워드는 즉시 칩 영역에 추가됨. 다른 poster 에서 사라진 키워드는 다음 페이지 진입 시 자동 정리.
    const handleUpdatePoster = useCallback(
        async (updatedPoster) => {
            if (!updatedPoster?.id) return;

            // 1) 리스트 즉시 반영
            setPosters((prev) => prev.map((p) => (p.id === updatedPoster.id ? updatedPoster : p)));

            // 2) 현재 열려있는 모달도 즉시 반영(스냅샷 갱신)
            if (selectedPosterId === updatedPoster.id) {
                setSelectedPosterSnapshot(updatedPoster);
                selectedPosterSnapshotRef.current = updatedPoster;
            }

            // 3) 키워드 전체 목록 — API 재조회 없이 메모리 patch.
            //    수정된 poster 의 keywords 중 기존 목록에 없는 신규만 append → 칩 영역 깜빡임 없음.
            const newKeywords = Array.isArray(updatedPoster.keywords) ? updatedPoster.keywords : [];
            if (newKeywords.length > 0) {
                setAllKeywords((prev) => {
                    const set = new Set(prev || []);
                    let changed = false;
                    for (const k of newKeywords) {
                        if (k && !set.has(k)) { set.add(k); changed = true; }
                    }
                    return changed ? [...set] : prev;
                });
            }

            notify("저장 즉시 반영 완료", "success");
        },
        [selectedPosterId]
    );

    const handleDeletedPoster = useCallback(async () => {
        setSelectedPosterId(null);
        setSelectedPosterSnapshot(null);
        selectedPosterSnapshotRef.current = null;

        await Promise.all([
            loadPostersPage(0, false, activeKeywords, activeProjectName, activeName),
            loadKeywords(),
            loadProjectOptions(),
        ]);
        notify("삭제되었습니다.", "success");
    }, [loadPostersPage, loadKeywords, loadProjectOptions, activeKeywords, activeProjectName, activeName]);

    // 숫자만 덜 진하게
    const renderKeywordLabel = useCallback((k) => {
        const parts = String(k).split(/(\d+)/g);
        return (
            <span>
        {parts.map((p, idx) => {
            const isNumber = /^\d+$/.test(p);
            if (!p) return null;
            return (
                <span
                    key={`${k}-p-${idx}`}
                    style={
                        isNumber
                            ? { fontWeight: 700, color: "rgba(17, 24, 39, 0.55)", letterSpacing: "-0.2px" }
                            : { fontWeight: 800, color: "rgba(17, 24, 39, 0.85)", letterSpacing: "-0.2px" }
                    }
                >
              {p}
            </span>
            );
        })}
      </span>
        );
    }, []);

    // 가나다 정렬
    const sortedKeywords = useMemo(() => {
        return [...allKeywords].sort((a, b) => String(a).localeCompare(String(b), "ko"));
    }, [allKeywords]);

    const filteredKeywords = useMemo(() => {
        const q = keywordQuery.trim().toLowerCase();
        if (!q) return sortedKeywords;
        return sortedKeywords.filter((k) => String(k).toLowerCase().includes(q));
    }, [sortedKeywords, keywordQuery]);

    // 활성 키워드를 맨 위로
    const prioritizedKeywords = useMemo(() => {
        const activeSet = new Set(activeKeywords);
        const actives = activeKeywords.slice();
        const rest = filteredKeywords.filter((k) => !activeSet.has(k));
        return [...actives, ...rest];
    }, [filteredKeywords, activeKeywords]);

    const KEYWORD_PREVIEW_COUNT = 40;
    const visibleKeywords = useMemo(() => {
        if (showAllKeywords) return prioritizedKeywords;
        return prioritizedKeywords.slice(0, KEYWORD_PREVIEW_COUNT);
    }, [prioritizedKeywords, showAllKeywords]);

    const autoCompleteOptions = useMemo(() => {
        const q = keywordQuery.trim().toLowerCase();
        const base = q ? sortedKeywords.filter((k) => String(k).toLowerCase().includes(q)) : sortedKeywords;
        const activeSet = new Set(activeKeywords);
        const withoutActive = base.filter((k) => !activeSet.has(k));
        return withoutActive.slice(0, 5);
    }, [sortedKeywords, keywordQuery, activeKeywords]);

    const handleSelectFromAutocomplete = useCallback((selected) => {
        if (!selected) return;
        const keyword = String(selected);
        setActiveKeywords((prev) => (prev.includes(keyword) ? prev : [keyword, ...prev]));
        setKeywordOpen(true);
        setShowAllKeywords(true);
        setAutoCompleteValue(null);
        setKeywordQuery("");
    }, []);

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Stack spacing={2.25}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography sx={ds.typography.pageTitle}>키비주얼 목록</Typography>
                        <Typography sx={{ ...ds.typography.pageSubtitle, mt: 0.5 }}>
                            키워드/프로젝트명으로 필터링하고 키비주얼을 미리보기/수정/삭제할 수 있습니다.
                        </Typography>
                    </Box>

                    <ActionButton
                        variant="refresh"
                        onClick={() => reloadDatas(false)}
                    />
                </Stack>

                {/* 검색 필터 */}
                <Paper sx={{ ...ds.components.external.card, p: 2 }}>
                    <Stack spacing={1.25}>
                        {/* 프로젝트명 */}
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "center" }}
                            justifyContent="space-between"
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>
                                    키비주얼명
                                </Typography>
                            </Stack>
                            <TextField
                                value={activeName}
                                onChange={(e) => setActiveName(e.target.value)}
                                placeholder="키비주얼명 검색 (부분 일치)"
                                size="small"
                                sx={{ width: { xs: "100%", sm: 360 }, ...ds.components.external.inputSoft }}
                            />
                        </Stack>

                        <Divider sx={{ my: 0.25 }} />

                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "center" }}
                            justifyContent="space-between"
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>
                                    프로젝트명
                                </Typography>
                            </Stack>
                            <AppAutocomplete
                                freeSolo
                                options={projectOptions}
                                value={activeProjectName}
                                inputValue={activeProjectName}
                                onChange={(v) => setActiveProjectName(v == null ? "" : String(v))}
                                onInputChange={(v) => setActiveProjectName(v)}
                                placeholder="프로젝트명 검색 (부분 일치)"
                                variant="outlined" disableUnderline={false}
                                textFieldSx={ds.components.external.inputSoft}
                                sx={{ width: { xs: "100%", sm: 360 } }}
                            />
                        </Stack>

                        <Divider sx={{ my: 0.25 }} />

                        {/* 키워드 */}
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "center" }}
                            justifyContent="space-between"
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>
                                    키워드
                                </Typography>

                                <Chip
                                    label={keywordOpen ? "접기" : "열기"}
                                    size="small"
                                    clickable
                                    onClick={() => setKeywordOpen((prev) => !prev)}
                                    sx={normalChipSx}
                                />
                            </Stack>

                            <AppAutocomplete
                                options={autoCompleteOptions}
                                value={autoCompleteValue}
                                inputValue={keywordQuery}
                                onInputChange={(v) => {
                                    setKeywordQuery(v);
                                    setShowAllKeywords(false);
                                }}
                                onChange={(v) => handleSelectFromAutocomplete(v)}
                                clearOnBlur={false}
                                placeholder="키워드 검색"
                                variant="outlined" disableUnderline={false}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <SearchRoundedIcon fontSize="small" />
                                    </InputAdornment>
                                }
                                textFieldSx={ds.components.external.inputSoft}
                                sx={{ width: { xs: "100%", sm: 360 } }}
                            />
                        </Stack>

                        <Collapse in={keywordOpen} timeout={180}>
                            <Stack spacing={1.25}>
                                {activeKeywords.length > 0 && (
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mr: 0.5 }}>
                                            활성
                                        </Typography>

                                        {activeKeywords.map((k) => (
                                            <Chip
                                                key={`active-${k}`}
                                                label={renderKeywordLabel(k)}
                                                size="small"
                                                clickable
                                                onClick={() => toggleKeyword(k)}
                                                sx={activeChipSx}
                                            />
                                        ))}

                                        <ActionButton
                                            variant="secondary"
                                            startIcon={false}
                                            onClick={clearKeywords}
                                            label="전체 해제"
                                            sx={{ ml: 0.5, px: 1.5 }}
                                        />
                                    </Stack>
                                )}

                                <Divider sx={{ my: 0.25 }} />

                                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                    <Chip
                                        label="전체"
                                        size="small"
                                        clickable
                                        onClick={clearKeywords}
                                        sx={activeKeywords.length === 0 ? activeChipSx : normalChipSx}
                                    />

                                    {loadingKeywords ? (
                                        <>
                                            <Skeleton variant="rounded" width={90} height={28} />
                                            <Skeleton variant="rounded" width={70} height={28} />
                                            <Skeleton variant="rounded" width={110} height={28} />
                                        </>
                                    ) : (
                                        visibleKeywords.map((k, i) => {
                                            const active = activeKeywords.includes(k);
                                            return (
                                                <Chip
                                                    key={`${k}-${i}`}
                                                    label={renderKeywordLabel(k)}
                                                    size="small"
                                                    clickable
                                                    onClick={() => toggleKeyword(k)}
                                                    sx={active ? activeChipSx : normalChipSx}
                                                />
                                            );
                                        })
                                    )}
                                </Stack>

                                {!loadingKeywords && prioritizedKeywords.length > KEYWORD_PREVIEW_COUNT && (
                                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                                        <ActionButton
                                            variant="secondary"
                                            startIcon={false}
                                            onClick={() => setShowAllKeywords((p) => !p)}
                                            label={showAllKeywords ? "접기" : `+ 더보기 (${prioritizedKeywords.length - KEYWORD_PREVIEW_COUNT}개)`}
                                            sx={{ px: 2 }}
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </Collapse>

                        <Divider sx={{ my: 0.5 }} />

                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: "text.secondary" }}>
                            표시: <b>{filteredPosters.length}</b>개 / 전체 <b>{posters.length}</b>개{" "}
                            {!loadingKeywords && (
                                <>
                                    · 키워드: <b>{filteredKeywords.length}</b>개
                                </>
                            )}
                        </Typography>
                    </Stack>
                </Paper>

                {/* 첫 로딩 — 그리드 영역 안에 inline LoadingDots (헤더/필터/칩은 즉시 보임). */}
                {loadingPosters && posters.length === 0 && (
                    <Box sx={{ py: 6 }}>
                        <LoadingDots />
                    </Box>
                )}

                {/* 데이터 0건 상태 — 필터로 인한 empty */}
                {!loadingPosters && filteredPosters.length === 0 && posters.length > 0 && (
                    <Box sx={{ textAlign: "center", py: 8, color: ds.colors.textMuted }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 0.5 }}>
                            결과가 없습니다
                        </Typography>
                        <Typography sx={{ fontSize: 13 }}>
                            필터를 변경하거나 초기화해 보세요
                        </Typography>
                    </Box>
                )}

                {/* 키비주얼 그리드 */}
                <Box
                    sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: {
                            xs: "repeat(2, minmax(0, 1fr))",
                            md: "repeat(3, minmax(0, 1fr))",
                            lg: "repeat(4, minmax(0, 1fr))",
                            xl: "repeat(5, minmax(0, 1fr))",
                        },
                    }}
                >
                    {/*
                      첫 로딩 시에는 위 inline LoadingDots 가 보이고 그리드는 자연스럽게 비움.
                      추가 로드 중에도 기존 카드는 그대로 유지하고 sentinel 의 LoadingDots compact 만 표시.
                    */}
                    {filteredPosters.map((poster) => {
                        return (
                            <Paper
                                key={poster.id}
                                onClick={() => {
                                    setSelectedPosterId(poster.id);
                                    setSelectedPosterSnapshot(poster);
                                    selectedPosterSnapshotRef.current = poster;
                                }}
                                sx={{
                                    ...ds.components.external.contentCard,
                                    p: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "relative",
                                        borderRadius: 3,
                                        overflow: "hidden",
                                        bgcolor: ds.colors.bg,
                                    }}
                                >
                                    {/* ✅ 이미지 (썸네일 우선, 없으면 원본 CDN) */}
                                    <Box
                                        component="img"
                                        src={pickThumbnailSrc(poster, API_REAL)}
                                        alt={poster.name}
                                        sx={{
                                            width: "100%",
                                            height: 340,
                                            display: "block",
                                            objectFit: "cover",
                                        }}
                                    />

                                    {/* ✅ 제목 오버레이 (회색/검정 배경을 절반 정도 연하게) */}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            p: 1.25,
                                            background: "linear-gradient(to bottom, rgba(0,0,0,0.28), rgba(0,0,0,0))",
                                        }}
                                    >
                                        <Typography
                                            noWrap
                                            sx={{
                                                color: "#fff",
                                                fontWeight: 800,
                                                fontSize: 14.5,
                                                letterSpacing: "-0.2px",
                                                textShadow: "0 1px 8px rgba(0,0,0,0.28)",
                                            }}
                                            title={poster.name}
                                        >
                                            {poster.name}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.2,
                                                color: "rgba(255,255,255,0.75)",
                                                fontWeight: 800,
                                                fontSize: 12,
                                                textShadow: "0 1px 8px rgba(0,0,0,0.28)",
                                            }}
                                        >
                                            등록 {poster.createdAt ? String(poster.createdAt).slice(0, 16).replace("T", " ") : "-"}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>

                {/* 무한 스크롤 sentinel — 화면에 들어오면 다음 페이지 자동 로드 */}
                {hasMore && (
                    <Box ref={sentinelRef} sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                        {loadingPosters ? (
                            <LoadingDots compact label="추가 불러오는 중" />
                        ) : (
                            <Box sx={{ height: 20 }} />
                        )}
                    </Box>
                )}

                {/* 키비주얼 모달 */}
                {selectedPosterId && selectedPoster && (
                    <KeyVisualModal
                        keyVisual={selectedPoster}
                        onClose={() => {
                            setSelectedPosterId(null);
                            setSelectedPosterSnapshot(null);
                            selectedPosterSnapshotRef.current = null;
                        }}
                        onUpdate={handleUpdatePoster}
                        onDeleted={handleDeletedPoster}
                        openSnack={notify}
                    />
                )}
            </Stack>

            {/*
              풀스크린 Backdrop 제거 — 첫 로드는 그리드 위쪽 inline LoadingDots, 추가 로드는 sentinel.
              loadingKeywords 는 키워드 영역의 Skeleton chip 으로 안내됨 (별도 표시 불필요).
            */}

        </Container>
    );
}
